import React, { useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref as storageRef, uploadBytes } from 'firebase/storage';
import { useAuth } from '../../contexts/AuthContext';
import { photoCategoryLabel } from './ServiceEvidenceGallery';

const allowed = new Set(['image/jpeg', 'image/png', 'image/webp']);
const ext = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };
export default function ServiceEvidenceUploader({ job, category, disabled = false }) {
  const { currentUser } = useAuth();
  const inputRef = useRef(null);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [retry, setRetry] = useState(null);

  async function register(item) {
    await httpsCallable(getFunctions(), 'registerServiceEvidence')({
      jobId: job.id, path: item.path, category, caption: item.caption,
    });
  }
  async function handleUpload(event) {
    const file = event.target.files?.[0];
    if (!file || busy || !currentUser?.uid) return;
    setError(''); setNotice(''); setRetry(null);
    if (!allowed.has(file.type) || file.size <= 0 || file.size > 6 * 1024 * 1024) {
      setError('Select a JPG, PNG, or WEBP image no larger than 6 MB.');
      event.target.value = '';
      return;
    }
    const path = `serviceEvidence/${job.id}/${category}/${crypto.randomUUID()}.${ext[file.type]}`;
    const item = { path, caption: caption.trim().slice(0, 140) };
    setBusy(true);
    try {
      await uploadBytes(storageRef(getStorage(), path), file, {
        contentType: file.type, customMetadata: { uploadedBy: currentUser.uid },
      });
      try {
        await register(item);
        setCaption(''); setNotice('Photo saved to the work order.');
      } catch (err) {
        setRetry(item);
        throw new Error(`Photo uploaded but not registered: ${err.message}. Click Retry registration.`);
      }
    } catch (err) { setError(err.message || 'Photo upload failed.'); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  }
  const retryRegistration = async () => {
    if (!retry) return;
    setBusy(true); setError('');
    try { await register(retry); setRetry(null); setNotice('Photo registration completed.'); }
    catch (err) { setError(err.message || 'Registration failed.'); }
    finally { setBusy(false); }
  };
  return <div className="rounded-lg border border-gray-300 bg-white p-4 space-y-3">
    <label className="block text-sm font-semibold text-[#111111]">{photoCategoryLabel[category]} photo
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment"
        disabled={disabled || busy} onChange={handleUpload} className="block mt-2 text-sm w-full" />
    </label>
    <label className="block text-sm">Photo note (optional)
      <input value={caption} onChange={e => setCaption(e.target.value)} maxLength={140}
        placeholder="Location, device, or condition pictured" className="mt-1 p-2 border rounded w-full text-[#111111]" />
    </label>
    {busy && <p role="status" className="text-sm">Uploading and verifying…</p>}
    {notice && <p role="status" className="text-sm text-green-800">{notice}</p>}
    {error && <p role="alert" className="text-sm text-red-800">{error}</p>}
    {retry && <button type="button" onClick={retryRegistration} disabled={busy}
      className="rounded border px-3 py-2 text-sm font-semibold">Retry registration</button>}
  </div>;
}
